// §0.5.4 ACNM Lab Flagging Thresholds — admin-configurable, no code deploy required.
// Stored in Firestore at settings/labFlaggingThresholds.
// All changes logged with admin user ID, timestamp, and previous value.

import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Spinner,
  Stack,
  Tag,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { useAuth } from '@hb/react'
import {
  DEFAULT_LAB_FLAGGING_THRESHOLDS,
  LAB_FLAGGING_THRESHOLDS_DOC,
  LabFlaggingThreshold,
  LabFlaggingThresholds,
  SETTINGS,
} from '@hb/shared'
import { doc, setDoc } from 'firebase/firestore'
import { useCallback, useMemo, useState } from 'react'
import { db } from '../../../../backend/db/db'
import { useDocument } from '../../../../backend/db/hooks'

const useLabFlaggingThresholdsAdmin = () => {
  const { data, loading } = useDocument<LabFlaggingThresholds>(
    SETTINGS,
    LAB_FLAGGING_THRESHOLDS_DOC,
  )
  const { user } = useAuth()

  const thresholds: Record<string, LabFlaggingThreshold> = useMemo(() => {
    const base = data?.thresholds ?? {}
    const merged: Record<string, LabFlaggingThreshold> = {}
    for (const [id, defaults] of Object.entries(DEFAULT_LAB_FLAGGING_THRESHOLDS)) {
      merged[id] = { ...defaults, ...base[id], id }
    }
    return merged
  }, [data])

  const updateThreshold = useCallback(
    async (id: string, updates: Partial<LabFlaggingThreshold>) => {
      if (!user?.id) return { error: 'Not authenticated' }
      const docRef = doc(db, SETTINGS, LAB_FLAGGING_THRESHOLDS_DOC)
      const current = thresholds[id]
      const now = new Date().toISOString()
      const changeLog = [
        ...(current.changeLog ?? []).slice(-19),
        { changedBy: user.id, changedAt: now, previousValue: { ...current } },
      ]
      try {
        await setDoc(
          docRef,
          {
            thresholds: {
              [id]: { ...current, ...updates, id, changeLog },
            },
          },
          { merge: true },
        )
        return { success: 'Updated' }
      } catch (e) {
        return { error: (e as Error).message }
      }
    },
    [thresholds, user],
  )

  return { thresholds, loading, updateThreshold }
}

const ThresholdRow = ({
  threshold,
  onUpdate,
}: {
  threshold: LabFlaggingThreshold
  onUpdate: (
    id: string,
    updates: Partial<LabFlaggingThreshold>,
  ) => Promise<{ success?: string; error?: string }>
}) => {
  const [editMin, setEditMin] = useState<string>(String(threshold.minValue ?? ''))
  const [editMax, setEditMax] = useState<string>(String(threshold.maxValue ?? ''))
  const [editNote, setEditNote] = useState(threshold.clinicalNote ?? '')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')

  const handleSave = async () => {
    setSaving(true)
    setMsg('')
    const updates: Partial<LabFlaggingThreshold> = {}
    if (threshold.minValue !== undefined)
      updates.minValue = parseFloat(editMin) || threshold.minValue
    if (threshold.maxValue !== undefined)
      updates.maxValue = parseFloat(editMax) || threshold.maxValue
    if (editNote !== threshold.clinicalNote) updates.clinicalNote = editNote
    const r = await onUpdate(threshold.id, updates)
    setMsg(r.success ?? r.error ?? '')
    setSaving(false)
  }

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius={6}
      p={3}
      bg="white"
      _dark={{ bg: 'gray.800', borderColor: 'gray.600' }}>
      <Flex justify="space-between" align="flex-start" gap={2} flexWrap="wrap">
        <Box flex={1} minW="200px">
          <Text fontWeight={600} fontSize="sm" fontFamily="Hero-New">
            {threshold.labName}
          </Text>
          <Text fontSize="xs" color="gray.500" mt={0.5}>
            {threshold.description}
          </Text>
          {threshold.unit && (
            <Badge mt={1} colorScheme="blue" variant="subtle" fontSize="2xs">
              {threshold.unit}
            </Badge>
          )}
        </Box>

        <Stack spacing={2} minW="200px">
          {threshold.minValue !== undefined && (
            <HStack>
              <Text fontSize="xs" color="gray.600" minW="80px">
                Min value
              </Text>
              <NumberInput size="xs" value={editMin} onChange={setEditMin} step={0.1} w="80px">
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </HStack>
          )}
          {threshold.maxValue !== undefined && (
            <HStack>
              <Text fontSize="xs" color="gray.600" minW="80px">
                Max value
              </Text>
              <NumberInput size="xs" value={editMax} onChange={setEditMax} step={0.1} w="80px">
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </HStack>
          )}
          {threshold.flaggedValues && (
            <HStack flexWrap="wrap">
              <Text fontSize="xs" color="gray.600" minW="80px">
                Flagged when
              </Text>
              <Flex gap={1} flexWrap="wrap">
                {threshold.flaggedValues.map(v => (
                  <Tag key={v} size="sm" colorScheme="red" variant="subtle">
                    {v}
                  </Tag>
                ))}
              </Flex>
            </HStack>
          )}
        </Stack>

        <Box minW="180px" flex={1}>
          <Text fontSize="xs" color="gray.500" mb={1}>
            Clinical note (shown to provider on flag)
          </Text>
          <Input size="xs" value={editNote} onChange={e => setEditNote(e.target.value)} />
        </Box>
      </Flex>

      <Flex mt={2} align="center" gap={3}>
        <Button size="xs" colorScheme="green" onClick={handleSave} isLoading={saving}>
          Save changes
        </Button>
        {msg && (
          <Text fontSize="xs" color={msg.startsWith('Updated') ? 'green.500' : 'red.500'}>
            {msg}
          </Text>
        )}
        {threshold.changeLog?.length ? (
          <Tooltip
            label={`Last changed ${threshold.changeLog.at(-1)?.changedAt?.slice(0, 10) ?? ''} by ${threshold.changeLog.at(-1)?.changedBy ?? ''}`}
            placement="top"
            hasArrow>
            <Text fontSize="2xs" color="gray.400" cursor="default">
              {threshold.changeLog.length} change{threshold.changeLog.length !== 1 ? 's' : ''}
            </Text>
          </Tooltip>
        ) : null}
      </Flex>
    </Box>
  )
}

export const LabFlaggingThresholdsAdminView = () => {
  const { thresholds, loading, updateThreshold } = useLabFlaggingThresholdsAdmin()

  if (loading) {
    return (
      <Flex p={8} justify="center">
        <Spinner />
      </Flex>
    )
  }

  return (
    <VStack align="stretch" spacing={3} p={4} h="100%" overflowY="auto">
      <Box>
        <Text fontFamily="Hero-New" fontWeight={700} fontSize="md">
          Lab Flagging Thresholds
        </Text>
        <Text fontSize="sm" color="gray.500" mt={1}>
          ACNM pregnancy-specific reference ranges. Changes take effect immediately and are logged
          with your user ID and timestamp. No code deploy required.
        </Text>
      </Box>
      <Divider />
      {Object.values(thresholds).map(t => (
        <ThresholdRow key={t.id} threshold={t} onUpdate={updateThreshold} />
      ))}
    </VStack>
  )
}
